import { PERMISSION_CATALOG, PERMISSION_GROUPS } from '@idc-forms/permissions/catalog'
import { CircleOff, CirclePlay, Info, Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { PATHS } from '@/app/router/routes'
import { DetailFieldRow } from '@/shared/components/detail-rows/detail-field-row'
import { UserChip } from '@/shared/components/detail-rows/user-chip'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/accordion'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { formatHumanDateTime } from '@/shared/utils/format-date'
import { Permission } from '../consts/permissions'
import { isPermissionVisible } from '../domain/visible-permission-groups'
import { useDeleteRoleMutation, useUpdateRoleMutation } from '../hooks/use-role-mutations'
import type { RoleAdminRow } from '../model/role-admin'
import { RoleActiveBadge } from './roles-admin-columns'

type RoleDetailPanelProps = {
  role: RoleAdminRow
}

const CATALOG_BY_PERMISSION = new Map(
  PERMISSION_CATALOG.map((entry) => [entry.permission as string, entry])
)
const _GROUP_LABELS = new Map(PERMISSION_GROUPS.map((group) => [group.key as string, group.label]))

/** Filas CRUD comparten label ("Usuarios"); se desambiguan con la columna. */
const COLUMN_LABELS: Record<string, string> = {
  read: 'Consultar',
  create: 'Crear',
  update: 'Actualizar',
  delete: 'Eliminar'
}
const AMBIGUOUS_LABELS = (() => {
  const seen = new Map<string, number>()
  for (const entry of PERMISSION_CATALOG) {
    const key = `${entry.group}:${entry.label}`
    seen.set(key, (seen.get(key) ?? 0) + 1)
  }
  return new Set([...seen.entries()].filter(([, count]) => count > 1).map(([key]) => key))
})()

type PermissionGroupSummary = { label: string; items: string[] }

/**
 * Permisos del rol como LISTADO legible agrupado por módulo del catálogo:
 * la etiqueta en español de cada permiso (con calificador Consultar/Crear/…
 * cuando varias filas CRUD comparten label). Los que el catálogo no expone
 * (legacy o administrados en otra matriz) van a "Otros" con su código crudo.
 */
function groupPermissions(permissions: string[]): PermissionGroupSummary[] {
  const byGroup = new Map<string, string[]>()
  // Un permiso de una funcion apagada no se nombra ni aunque el rol lo tenga: la migracion que
  // siembra los de la app movil se los asigna a ADMINISTRACION y COMERCIAL OPERACIONES, asi que
  // sin este filtro el detalle del rol delata la funcion que el flag esconde.
  for (const permission of permissions.filter(isPermissionVisible)) {
    const entry = CATALOG_BY_PERMISSION.get(permission)
    const group = entry?.group ?? 'other'
    let label = entry?.label ?? permission
    if (entry && AMBIGUOUS_LABELS.has(`${entry.group}:${entry.label}`)) {
      const qualifier = COLUMN_LABELS[entry.column]
      if (qualifier) label = `${entry.label}: ${qualifier}`
    }
    byGroup.set(group, [...(byGroup.get(group) ?? []), label])
  }
  // El orden de los grupos es el del catálogo (tabs de la matriz); Otros al final.
  const ordered: PermissionGroupSummary[] = []
  for (const group of PERMISSION_GROUPS) {
    const items = byGroup.get(group.key)
    if (items) ordered.push({ label: group.label, items: items.sort((a, b) => a.localeCompare(b)) })
  }
  const other = byGroup.get('other')
  if (other) ordered.push({ label: 'Otros', items: other.sort((a, b) => a.localeCompare(b)) })
  return ordered
}

/**
 * Panel de detalle del rol: resumen + acciones + acordeones (permisos por
 * grupo, actividad). La EDICIÓN vive en la página completa (create-update-role)
 * porque aloja la PermissionMatrix y la matriz por estado — espejo de la
 * excepción de states. Eliminar desasigna el rol de todos sus usuarios.
 */
export function RoleDetailPanel({ role }: RoleDetailPanelProps) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const closePanel = useSidePanelStore((s) => s.closePanel)
  const navigate = useNavigate()
  const updateMutation = useUpdateRoleMutation()
  const deleteMutation = useDeleteRoleMutation()
  const { has } = usePermissions()

  const toggleActive = () => {
    updateMutation.mutate({ id: role._id, payload: { active: !role.active } })
  }

  // Docs sembrados antes de existir estos campos no los traen — con fallback.
  const permissions = role.permissions ?? []
  const permissionGroups = groupPermissions(permissions)

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody className="gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold leading-tight">{role.name}</h3>
            {role.description && (
              <p className="text-sm text-muted-foreground">{role.description}</p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <DetailFieldRow label="Estado" value={<RoleActiveBadge active={role.active} />} />
            <DetailFieldRow label="Permisos" value={String(permissions.length)} />
            <DetailFieldRow
              label="Permisos por estado"
              value={String(role.statePermissionsCount ?? 0)}
            />
            <DetailFieldRow label="Usuarios asignados" value={String(role.usersCount ?? 0)} />
          </div>
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection>
        <SidePanelActions
          maxInline={2}
          actions={[
            ...(has(Permission.UpdateRoles)
              ? [
                  {
                    label: 'Editar rol',
                    icon: <Pencil className="h-4 w-4" />,
                    variant: 'default' as const,
                    primary: true,
                    onClick: () => {
                      closePanel()
                      void navigate(`${PATHS.admin.business.roles}/${role._id}`)
                    }
                  },
                  {
                    label: role.active ? 'Desactivar rol' : 'Activar rol',
                    icon: role.active ? (
                      <CircleOff className="h-4 w-4" />
                    ) : (
                      <CirclePlay className="h-4 w-4" />
                    ),
                    variant: 'outline' as const,
                    disabled: updateMutation.isPending,
                    onClick: toggleActive
                  }
                ]
              : []),
            ...(has(Permission.DeleteRoles)
              ? [
                  {
                    label: 'Eliminar rol',
                    icon: <Trash2 className="h-4 w-4" />,
                    variant: 'destructive' as const,
                    disabled: deleteMutation.isPending,
                    onClick: () => {
                      setConfirmDelete(true)
                    }
                  }
                ]
              : [])
          ]}
        />
      </SidePanelSection>

      <Tabs defaultValue="more-info">
        <TabsList className="w-full justify-normal rounded-none">
          <TabsTrigger className="flex gap-1" value="more-info">
            <Info className="size-4" /> Más información
          </TabsTrigger>
        </TabsList>
        <TabsContent value="more-info">
          <Accordion type="multiple" defaultValue={['permissions', 'activity']}>
            <AccordionItem value="permissions">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Permisos ({permissions.length})
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-3 pt-2 pl-3 pr-2">
                  {permissionGroups.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      Este rol no tiene permisos asignados.
                    </p>
                  )}
                  {permissionGroups.map((group) => (
                    <div key={group.label} className="flex flex-col gap-1.5">
                      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        {group.label} ({group.items.length})
                      </span>
                      <ul className="flex flex-wrap gap-1">
                        {group.items.map((item) => (
                          <li
                            key={item}
                            className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80"
                          >
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="activity">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Actividad
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow label="Creado" value={formatHumanDateTime(role.createdAt)} />
                  <DetailFieldRow
                    label="Creado por"
                    value={<UserChip name={role.createdBy?.fullname} />}
                  />
                  <DetailFieldRow label="Actualizado" value={formatHumanDateTime(role.updatedAt)} />
                  <DetailFieldRow
                    label="Actualizado por"
                    value={<UserChip name={role.updatedBy?.fullname} />}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar rol</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará “{role.name}” y se les quitará a los {role.usersCount ?? 0} usuario(s)
              que lo tienen asignado. Esos usuarios perderán los permisos que este rol les daba.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteMutation.mutate(role._id, {
                  onSuccess: () => {
                    closePanel()
                  }
                })
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
